/** Spiraling particle/line effect — vortexes, implosions, swirling energy.
 *
 * Renders spiral arms that rotate around a center point with configurable
 * radius change over time (inward collapse or outward expansion). Canvas2D
 * provides antialiased curves in pixelArt mode.
 *
 * Unlike OrbitalSprites (fixed-radius orbit), SpiralVortex animates the
 * radius continuously — arms can spiral inward (implosion), outward
 * (explosion), or pulse between radii.
 */
import Phaser from 'phaser';
import { VfxPrimitive, VfxPrimitiveConfig } from './VfxPrimitive';
import { CRISP_SCALE } from '../../ui/CrispUtil';

export interface SpiralVortexConfig extends VfxPrimitiveConfig {
  /** Number of spiral arms. */
  armCount: number;
  /** Inner radius in logical pixels. */
  innerRadius: number;
  /** Outer radius in logical pixels. */
  outerRadius: number;
  /** Stroke width of each arm, in logical pixels. */
  lineWidth: number;
  /** Rotation speed in radians per second. Negative = clockwise. */
  angularSpeed: number;
  /** Spiral mode: 'inward' collapses, 'outward' expands, 'pulse' oscillates. */
  mode: 'inward' | 'outward' | 'pulse';
  /** Duration of one inward/outward cycle in ms (for pulse mode, one full oscillation). */
  cycleDurationMs: number;
  /** Number of coils each arm makes between inner and outer radius. */
  coils: number;
  /** Dot count along each arm (resolution). */
  armResolution: number;
  /** Duration of the form-in animation in ms. */
  formDurationMs: number;
  /** Duration of the fade-out animation in ms. */
  fadeDurationMs: number;
}

type VortexState = 'inactive' | 'forming' | 'sustained' | 'fading';

export class SpiralVortex extends VfxPrimitive<SpiralVortexConfig> {
  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private canvasTexture!: Phaser.Textures.CanvasTexture;
  private display!: Phaser.GameObjects.Image;
  private textureKey!: string;

  private state: VortexState = 'inactive';
  private rotation = 0;
  private cyclePhase = 0;
  private currentAlpha = 0;

  private transitionTween: Phaser.Tweens.Tween | null = null;

  protected allocatePool(): void {
    const logicalSize = this.config.outerRadius * 2 + this.config.lineWidth * 2 + 4;
    const canvasPx = Math.ceil(logicalSize * CRISP_SCALE);
    this.textureKey = `_spiral-vortex-${Date.now()}`;
    this.canvas = document.createElement('canvas');
    this.canvas.width = canvasPx;
    this.canvas.height = canvasPx;
    this.ctx = this.canvas.getContext('2d')!;

    this.canvasTexture = this.scene.textures.addCanvas(this.textureKey, this.canvas)!;
    this.canvasTexture.setFilter(Phaser.Textures.FilterMode.LINEAR);

    this.display = this.scene.add.image(0, 0, this.textureKey)
      .setOrigin(0.5, 0.5)
      .setDisplaySize(logicalSize, logicalSize)
      .setBlendMode(this.config.blendMode ?? Phaser.BlendModes.NORMAL);
    this.display.setVisible(false).setActive(false);
    this.pool.push(this.display);
  }

  play(x: number, y: number, depth: number): void {
    if (this.state === 'sustained' || this.state === 'forming') return;
    this.killTransition();
    this.state = 'forming';
    this.rotation = 0;
    this.cyclePhase = 0;

    this.display
      .setPosition(x, y)
      .setDepth(depth + (this.config.depthOffset ?? 0))
      .setVisible(true)
      .setActive(true);

    const obj = { alpha: 0 };
    this.transitionTween = this.scene.tweens.add({
      targets: obj,
      alpha: 1,
      duration: this.config.formDurationMs,
      ease: 'Quad.easeOut',
      onUpdate: () => {
        this.currentAlpha = obj.alpha;
      },
      onComplete: () => {
        this.state = 'sustained';
        this.currentAlpha = 1;
        this.transitionTween = null;
      },
    });
  }

  stop(): void {
    if (this.state === 'inactive' || this.state === 'fading') return;
    this.killTransition();
    this.state = 'fading';

    const obj = { alpha: this.currentAlpha };
    this.transitionTween = this.scene.tweens.add({
      targets: obj,
      alpha: 0,
      duration: this.config.fadeDurationMs,
      ease: 'Quad.easeIn',
      onUpdate: () => {
        this.currentAlpha = obj.alpha;
      },
      onComplete: () => {
        this.state = 'inactive';
        this.display.setVisible(false).setActive(false);
        this.transitionTween = null;
      },
    });
  }

  /** Follow a moving entity each frame. Drives rotation and spiral animation. */
  updatePosition(x: number, y: number, depth: number, delta: number): void {
    if (this.state === 'inactive') return;
    this.display.setPosition(x, y)
      .setDepth(depth + (this.config.depthOffset ?? 0));

    const deltaSec = delta / 1000;
    this.rotation += this.config.angularSpeed * deltaSec;
    this.cyclePhase = (this.cyclePhase + delta / this.config.cycleDurationMs) % 1;

    this.redraw();
  }

  destroy(): void {
    this.killTransition();
    if (this.textureKey && this.scene.textures.exists(this.textureKey)) {
      this.scene.textures.remove(this.textureKey);
    }
    super.destroy();
  }

  private killTransition(): void {
    if (this.transitionTween) {
      this.transitionTween.destroy();
      this.transitionTween = null;
    }
  }

  /** Redraw spiral arms onto the canvas texture. */
  private redraw(): void {
    const { ctx, canvas, config } = this;
    const canvasW = canvas.width;
    const canvasH = canvas.height;
    ctx.clearRect(0, 0, canvasW, canvasH);

    const cx = canvasW / 2;
    const cy = canvasH / 2;
    const innerR = config.innerRadius * CRISP_SCALE;
    const outerR = config.outerRadius * CRISP_SCALE;
    const lw = config.lineWidth * CRISP_SCALE;

    // Compute current radius range based on mode and cycle phase
    const radiusFraction = this.getRadiusFraction();
    const currentInner = innerR;
    const currentOuter = innerR + (outerR - innerR) * radiusFraction;

    const armAngleStep = (Math.PI * 2) / config.armCount;
    const resolution = config.armResolution;
    const coils = config.coils;

    ctx.strokeStyle = `rgba(255, 255, 255, ${this.currentAlpha})`;
    ctx.lineWidth = lw;
    ctx.lineCap = 'round';

    for (let arm = 0; arm < config.armCount; arm++) {
      const baseAngle = this.rotation + arm * armAngleStep;

      ctx.beginPath();
      let started = false;
      for (let i = 0; i <= resolution; i++) {
        const t = i / resolution;
        const r = currentInner + (currentOuter - currentInner) * t;
        const angle = baseAngle + t * coils * Math.PI * 2;
        const px = cx + Math.cos(angle) * r;
        const py = cy + Math.sin(angle) * r;

        // Fade alpha along the arm — bright at inner, fading at outer
        if (!started) {
          ctx.moveTo(px, py);
          started = true;
        } else {
          ctx.lineTo(px, py);
        }
      }
      ctx.stroke();
    }

    this.display.setTint(config.tint);
    this.canvasTexture.refresh();
  }

  /** Get the fraction of outer radius currently visible, based on mode and phase. */
  private getRadiusFraction(): number {
    switch (this.config.mode) {
      case 'inward':
        // Outer radius shrinks inward over the cycle
        return 1 - this.cyclePhase;
      case 'outward':
        // Inner radius expands outward over the cycle
        return this.cyclePhase;
      case 'pulse':
        // Oscillate between inner and outer
        return 0.5 + 0.5 * Math.sin(this.cyclePhase * Math.PI * 2);
      default:
        return 1;
    }
  }
}
